import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Button, Grid, ListItemButton, styled, Typography } from '@mui/material';
import React from 'react';

import theme from '../../styles/theme';

/**------------------------------------------------------
 * Componentes que solo se utilizan una vez. No se hacen reutilizables porque son muy especificos.
 *------------------------------------------------------
 */

/** ------------------------------------------------------
 *
 * HEADER
 *
 * -------------------------------------------------------
 */

/**
 * Menú de navegación iterativo para la cabecera (incluye divisores)
 */
export const NavigationMenuHeader = ({ menuItems, activeSearch, onSearchToggle }) => {
  return (
    <Grid container alignItems="center" justifyContent="center">
      {menuItems.map((item, index) => (
        <React.Fragment key={index}>
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: item.isSearchGroup ? 1 : 0 }}>
            {item.preIcon && item.preIcon}
            <ButtonNavegacionHeader
              startIcon={item.startIcon}
              onClick={() => {
                if (item.isSearchGroup) {
                  onSearchToggle(item.id);
                } else if (item.onClick) {
                  item.onClick();
                }
              }}
              isActive={item.isSearchGroup ? activeSearch === item.id : item.isActive}
            >
              {item.label}
            </ButtonNavegacionHeader>
          </Grid>
          {item.showDivider && (
            <Grid item>
              <Box sx={{ width: '1px', height: '20px', backgroundColor: 'divider', mx: 2 }} />
            </Grid>
          )}
        </React.Fragment>
      ))}
    </Grid>
  );
};

/**
 * Pestaña de navegación para el sub-header (diseño de pestaña moderno con acento superior)
 */
export const ButtonNavegacionHeader = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ isActive }) => ({
  height: 42,
  minWidth: 130,
  padding: '6px 18px',
  borderRadius: '8px 8px 0 0',
  textTransform: 'none',
  fontSize: '0.86rem',
  fontWeight: isActive ? 700 : 600,
  transition: 'all 0.15s ease-in-out',
  position: 'relative',
  bottom: '-1px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',

  // Pestaña activa: fondo blanco puro, borde superior teal, y fondo inferior blanco que monta sobre la línea
  backgroundColor: isActive ? '#ffffff' : 'transparent',
  color: isActive ? '#0f766e' : '#334155',
  borderTop: isActive ? '3px solid #0f766e' : '3px solid transparent',
  borderLeft: isActive ? '1px solid #b2ded7' : '1px solid transparent',
  borderRight: isActive ? '1px solid #b2ded7' : '1px solid transparent',
  borderBottom: isActive ? '1px solid #ffffff' : 'none',
  boxShadow: isActive ? '0 -2px 6px rgba(15, 118, 110, 0.08)' : 'none',
  zIndex: isActive ? 2 : 1,

  '&:hover': {
    backgroundColor: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.65)',
    color: '#0f766e',
    borderTop: isActive ? '3px solid #0f766e' : '3px solid rgba(15, 118, 110, 0.35)',
  },

  '& .MuiButton-startIcon': {
    marginRight: 7,
    marginLeft: 0,
    color: isActive ? '#0f766e' : '#64748b',
    '& svg': {
      fontSize: '1.15rem',
      transition: 'color 0.15s ease-in-out',
    },
  },
  '&:hover .MuiButton-startIcon': {
    color: '#0f766e',
  },
}));

/**
 * Button para acciones en el header (Config, Logout)
 */
export const ButtonHeaderApp = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'exit',
})(({ exit }) => ({
  color: theme.palette.primary.contrastText,
  border: '0.5px solid',
  borderColor: theme.palette.divider,
  textTransform: 'none',
  padding: theme.spacing(1),
  margin: theme.spacing(0.25),
  borderRadius: theme.borderRadius,
  cursor: 'pointer',
  textAlign: 'center',
  '&:hover': {
    backgroundColor: theme.palette.hover.main,
    color: exit ? theme.palette.error.main : theme.palette.text.primary,
    '& > *': {
      color: exit ? theme.palette.error.main : theme.palette.text.primary,
    },
  },
  '& .MuiButton-startIcon': { margin: 0 },
  '& .MuiButton-endIcon': { marginLeft: theme.spacing(1) },
}));

/** ------------------------------------------------------
 *
 * JUGADOR
 *
 * -------------------------------------------------------
 */

/**
 * Box que contiene un Icono, un Componente hijo (Select generalemente) y opcionalmente un divisor a su lado derecho.
 */
export const IconSelectBoxApp = ({ icon, children, showDivider = true }) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {icon && <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{icon}</Box>}
        {children}
      </Box>
      {showDivider && <Box sx={{ borderLeft: '1px solid rgba(0,0,0,0.1)', height: '25px' }} />}
    </>
  );
};

/**
 * Button para modal de observaciones
 */
export const BoxBotonObservaciones = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'success',
})(({ success }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.borderRadius,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: success ? theme.palette.success.main : theme.palette.fail.main,
  },
}));

/** ------------------------------------------------------
 *
 * INICIO SESION
 *
 * -------------------------------------------------------
 */

/**
 * Box para el header de Inicio Sesion (logo)
 */
export const BoxHeaderInicioSesion = styled(Box)(() => ({
  width: '400px',
  height: 'auto',
  display: 'block',
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: 1,
}));

/** ------------------------------------------------------
 *
 * PARTIDOS
 *
 * -------------------------------------------------------
 */

/**
 * Typography para resultado de partido
 */
export const TypographyResultadoPartido = styled(Typography)(() => ({
  fontSize: '3rem',
  fontWeight: 'bold',
  textAlign: 'center',
}));

/** ------------------------------------------------------
 *
 * LISTAS
 *
 * -------------------------------------------------------
 */

/**
 * Sección plegable (ListItemButton) para categorizar listas (Mis Listas, Compartidas, etc)
 */
export const CollapsibleListSectionApp = ({ title, expanded, onToggle }) => {
  return (
    <ListItemButton
      onClick={onToggle}
      sx={{ px: 2, py: 0.5, borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}
      disableRipple
    >
      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'text.secondary', lineHeight: 1, flexGrow: 1 }}>
        {title}
      </Typography>
      {expanded ? (
        <ExpandLess sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
      ) : (
        <ExpandMore sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
      )}
    </ListItemButton>
  );
};

/** ------------------------------------------------------
 *
 * PLANIFICADOR DE PLANTILLA
 *
 * -------------------------------------------------------
 */

/**
 * Ficha arrastrable de un jugador en el planificador (sidebar y campo)
 */
export const FichaJugadorPlanWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['rol', 'enCampo', 'asignado', 'esFichaje', 'fuente'].includes(prop),
})(({ theme, enCampo = false, asignado = false, fuente = 'interno' }) => ({
  backgroundColor:
    fuente === 'externo'
      ? asignado
        ? '#e2eefcff'
        : theme.palette.info.light
      : asignado
        ? '#f1f5f9'
        : enCampo
          ? 'rgba(255,255,255,0.95)'
          : 'white',
  border: `1px solid ${theme.palette.divider}`,
  padding: enCampo ? theme.spacing(0.4, 0.6) : theme.spacing(0.5, 0.8),
  borderRadius: theme.borderRadius,
  display: 'flex',
  alignItems: 'center',
  cursor: asignado ? 'default' : 'grab',
  transition: 'box-shadow 0.15s ease, transform 0.15s ease',
  boxShadow: theme.shadows[1],
  width: enCampo ? '100%' : 'auto',
  '&:active': { cursor: asignado ? 'default' : 'grabbing' },
  '&:hover': {
    boxShadow: asignado ? theme.shadows[1] : theme.shadows[2],
    transform: asignado ? 'none' : 'translateY(-1px)',
  },
}));

/**
 * Slot de drop zone para una posicion + rol (Titular/Suplente/Alternativa) en el campogram
 */
export const SlotRolPlan = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ocupado',
})(({ ocupado = false }) => ({
  width: 240,
  minHeight: 34,
  backgroundColor: ocupado ? 'transparent' : 'rgba(255, 255, 255, 0.12)',
  border: ocupado ? '1px solid transparent' : '1px dashed rgba(255, 255, 255, 0.38)',
  borderRadius: theme.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  transition: 'background-color 0.15s',
  '&:hover': {
    backgroundColor: ocupado ? 'transparent' : 'rgba(255, 255, 255, 0.25)',
    borderColor: ocupado ? 'transparent' : 'rgba(255, 255, 255, 0.65)',
  },
}));

/**
 * El campo de futbol visual del planificador
 */
export const CampogramPlan = styled(Box)(() => ({
  width: '100%',
  height: '80vh',
  maxHeight: 720,
  backgroundColor: '#2d7a3a',
  border: `3px solid ${theme.palette.divider}`,
  borderRadius: theme.borderRadius + 2,
  position: 'relative',
  backgroundImage: [
    'linear-gradient(to bottom, transparent 49.8%, rgba(255,255,255,0.55) 49.8%, rgba(255,255,255,0.55) 50.2%, transparent 50.2%)',
    'repeating-linear-gradient(0deg, transparent, transparent 10%, rgba(255,255,255,0.05) 10%, rgba(255,255,255,0.05) 20%)',
  ].join(', '),
  boxShadow: theme.shadows[2],
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '18%',
    aspectRatio: '1',
    border: '2px solid rgba(255, 255, 255, 0.45)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 6,
    height: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
}));
